import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes, faSpinner } from "@fortawesome/free-solid-svg-icons";
import "./GenerateContentModal.css";
import CopilotManualModal from "./CopilotManualModal";

const GenerateContentModal = ({ show, onClose, onGenerate, hasAIAccess }) => {
  const [description, setDescription] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [showCopilotModal, setShowCopilotModal] = useState(false);
  const [questionCounts, setQuestionCounts] = useState({
    "Multiple Choice": 0,
    "Multiple Checkbox": 0,
    "Fill in the Blanks": 0,
    Pairing: 0,
    "Sentence Ordering": 0,
    "Spell the Word": 0,
    "Open-Ended Text": 0,
    "Audio Recording Task": 0,
  });

  const handleCountChange = (type, value) => {
    setQuestionCounts((prev) => ({
      ...prev,
      [type]: Math.max(0, parseInt(value) || 0),
    }));
  };

  const handleGenerate = async () => {
    setIsGenerating(true);
    try {
      await onGenerate(description, questionCounts);
    } finally {
      setIsGenerating(false);
      onClose();
    }
  };

  const handleCopilotGenerate = (materials) => {
    // This function will be called from CopilotManualModal
    // We need to add the materials directly to the module
    if (materials && materials.length > 0) {
      // Call the parent's onGenerate with the processed materials
      onGenerate(null, null, materials);
    }
  };

  const handleOpenCopilotModal = () => {
    setShowCopilotModal(true);
  };

  const handleCloseCopilotModal = () => {
    setShowCopilotModal(false);
  };

  if (!show) return null;

  return (
    <>
      <div className="generate-content-modal-overlay">
        <div className="generate-content-modal">
          <button className="close-button" onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>

          <h2>Generate Content</h2>

          <div className="description-section">
            <h3>Describe what content you need:</h3>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={`Provide details to get well-aligned questions. Think about:

What will these questions be used for?
What level are your students?
What should they learn or demonstrate?
Any specific topics, themes, or special interests?`}
            />
          </div>

          <div className="question-types-section">
            <h3 className="section-title">
              Select number of questions for each type:
            </h3>
            <div className="question-types-grid">
              {Object.entries(questionCounts).map(([type, count]) => (
                <div key={type} className="question-type-row">
                  <label>{type}</label>
                  <div className="count-control">
                    <button onClick={() => handleCountChange(type, count - 1)}>
                      -
                    </button>
                    <input
                      type="number"
                      value={count}
                      onChange={(e) => handleCountChange(type, e.target.value)}
                      min="0"
                    />
                    <button onClick={() => handleCountChange(type, count + 1)}>
                      +
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="generation-buttons">
            <button
              className={`generate-button ${!hasAIAccess ? "disabled" : ""}`}
              onClick={handleGenerate}
              disabled={
                !hasAIAccess ||
                isGenerating ||
                !description.trim() ||
                !Object.values(questionCounts).some((count) => count > 0)
              }
              title={
                !hasAIAccess
                  ? "Upgrade to a Plus account to use AI generation"
                  : "Generate content with our AI"
              }
            >
              {isGenerating ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} spin /> Generating...
                </>
              ) : (
                "Generate with AI"
              )}
              {!hasAIAccess && <span className="upgrade-badge">Plus</span>}
            </button>

            <button
              className="copilot-button"
              onClick={handleOpenCopilotModal}
              disabled={
                !description.trim() ||
                !Object.values(questionCounts).some((count) => count > 0)
              }
            >
              <div className="copilot-icon">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm0 22C6.486 22 2 17.514 2 12S6.486 2 12 2s10 4.486 10 10-4.486 10-10 10z" />
                  <path d="M12 6c-3.309 0-6 2.691-6 6s2.691 6 6 6 6-2.691 6-6-2.691-6-6-6zm0 10c-2.206 0-4-1.794-4-4s1.794-4 4-4 4 1.794 4 4-1.794 4-4 4z" />
                  <circle cx="12" cy="12" r="2" />
                </svg>
              </div>
              Manual AI Generation
            </button>
          </div>
        </div>
      </div>

      <CopilotManualModal
        show={showCopilotModal}
        onClose={handleCloseCopilotModal}
        description={description}
        questionCounts={questionCounts}
        onGenerate={handleCopilotGenerate}
      />
    </>
  );
};

export default GenerateContentModal;
